import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner, Tab, Tabs } from 'react-bootstrap';
import { BsArrowClockwise } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { useGetConfigQuery } from 'services/api';
import { LG_COLOR } from 'containers/App/constants';
import {
  useGetMosaicFetchControlQuery,
  useUpdateMosaicFetchControlMutation,
} from 'services/api/adminAdsureMonitoring';
import { useFetchMonitoringAnalyticsQuery } from 'services/api/creativePreview';
import { useAdsureMonitoringPodsQuery } from 'services/api/creativeServer';
import useHeader from 'containers/App/Header/useHeader';
import { DEFAULT_MONITORING_SOURCES } from './constants';
import InflowTab from './InflowTab';
import OutflowTab from './OutflowTab';
import PodMonitoringTab from './PodMonitoringTab';
import { AdsureMonitoringStyles } from './style';
import { AnalyticsPeriod, PodInfo } from './types';
import {
  getMonthStartDateString,
  getSourceLabel,
  getTodayDateString,
} from './utils';

function formatControlTimestamp(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function MosaicFetchControlCard() {
  const [pendingPaused, setPendingPaused] = useState<boolean | null>(null);
  const {
    data: mosaicFetchControl,
    isLoading,
    isFetching,
    error,
  } = useGetMosaicFetchControlQuery();
  const [updateMosaicFetchControl, updateState] =
    useUpdateMosaicFetchControlMutation();

  const isPaused = Boolean(mosaicFetchControl?.paused);
  const isBusy = isLoading || isFetching || updateState.isLoading;
  const nextLabel = pendingPaused ? 'pause' : 'start';
  const updatedAt = formatControlTimestamp(mosaicFetchControl?.updatedAt);

  const handleConfirm = async () => {
    if (pendingPaused === null) return;

    try {
      await updateMosaicFetchControl({ paused: pendingPaused }).unwrap();
      toast.success(
        pendingPaused
          ? 'Mosaic fetching paused.'
          : 'Mosaic fetching resumed.',
      );
      setPendingPaused(null);
    } catch {
      toast.error('Unable to update Mosaic fetch control.');
    }
  };

  return (
    <>
      <div className="adsure-mosaic-control">
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <div className="adsure-header-metric-label">
              Mosaic Fetch Control
            </div>
            <div className="adsure-mosaic-control-copy">
              Pause creative fetching during Mosaic bulk updates.
            </div>
          </div>
          <Badge bg={isPaused ? 'warning' : 'success'} text={isPaused ? 'dark' : undefined}>
            {isPaused ? 'Paused' : 'Active'}
          </Badge>
        </div>
        <div className="adsure-mosaic-toggle-row">
          <Form.Check
            type="switch"
            id="pause-fetch-from-mosaic"
            label="Pause Fetch from Mosaic"
            checked={isPaused}
            disabled={isBusy || Boolean(error)}
            onChange={() => setPendingPaused(!isPaused)}
          />
          {isBusy ? <Spinner animation="border" size="sm" /> : null}
        </div>
        {updatedAt || mosaicFetchControl?.updatedBy ? (
          <div className="adsure-mosaic-control-meta">
            {updatedAt ? `Updated ${updatedAt}` : ''}
            {updatedAt && mosaicFetchControl?.updatedBy ? ' by ' : ''}
            {mosaicFetchControl?.updatedBy || ''}
          </div>
        ) : null}
        {error ? (
          <div className="adsure-mosaic-control-error">
            Unable to load Mosaic fetch status.
          </div>
        ) : null}
      </div>

      <Modal
        centered
        show={pendingPaused !== null}
        onHide={() => setPendingPaused(null)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {pendingPaused ? 'Pause' : 'Start'} Fetch from Mosaic?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to {nextLabel} fetching creatives from Mosaic?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setPendingPaused(null)}
            disabled={updateState.isLoading}
          >
            Cancel
          </Button>
          <Button
            className="adsure-confirm-control-button"
            onClick={handleConfirm}
            disabled={updateState.isLoading}
            style={{
              backgroundColor: LG_COLOR,
              borderColor: LG_COLOR,
              color: '#fff',
            }}
          >
            {updateState.isLoading ? 'Saving...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default function AdsureMonitoring() {
  useHeader('AdSure Monitoring');

  const [activeTab, setActiveTab] = useState('pod-monitoring');
  const [podSearch, setPodSearch] = useState('');
  const [period, setPeriod] = useState<AnalyticsPeriod>('day');
  const [startDate, setStartDate] = useState(getMonthStartDateString());
  const [endDate, setEndDate] = useState(getTodayDateString());
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedAnalyticsPod, setSelectedAnalyticsPod] = useState('');

  const {
    data: podsResponse,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useAdsureMonitoringPodsQuery(undefined, {
    pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
  });
  const { data: appConfig } = useGetConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const monitoringSources = useMemo(() => {
    const sources = appConfig?.monitoringSources || [];
    return sources.length ? sources : DEFAULT_MONITORING_SOURCES;
  }, [appConfig?.monitoringSources]);

  const analyticsSourcesParam = useMemo(() => {
    if (!monitoringSources.length) return '';
    if (selectedSource === 'all') return monitoringSources.join(',');
    return selectedSource || monitoringSources[0];
  }, [monitoringSources, selectedSource]);

  const {
    data: analytics,
    isLoading: isAnalyticsLoading,
    isFetching: isAnalyticsFetching,
    error: analyticsError,
  } = useFetchMonitoringAnalyticsQuery(
    {
      period,
      startDate,
      endDate,
      sources: analyticsSourcesParam,
    },
    {
      skip: !analyticsSourcesParam,
      pollingInterval: 60000,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    const availablePods = analytics?.availablePods || [];
    if (!availablePods.length) {
      if (selectedAnalyticsPod !== '') setSelectedAnalyticsPod('');
      return;
    }

    if (!selectedAnalyticsPod || !availablePods.includes(selectedAnalyticsPod)) {
      setSelectedAnalyticsPod(availablePods[0]);
    }
  }, [analytics?.availablePods, selectedAnalyticsPod]);

  const pods = useMemo(
    () =>
      Object.entries((podsResponse || {}) as Record<string, PodInfo>).sort(
        ([left], [right]) => left.localeCompare(right),
      ),
    [podsResponse],
  );

  const filteredPods = useMemo(() => {
    const normalizedSearch = podSearch.trim().toLowerCase();

    if (!normalizedSearch) return pods;

    return pods.filter(([podName]) =>
      podName.toLowerCase().includes(normalizedSearch),
    );
  }, [podSearch, pods]);

  return (
    <AdsureMonitoringStyles>
      <div className="adsure-monitoring-shell">
        <div className="adsure-monitoring-header">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div>
              <div className="adsure-monitoring-eyebrow">Admin Center</div>
              <h2 className="adsure-monitoring-title">
                AdSure Monitoring
              </h2>
              <p className="adsure-monitoring-subtitle">
                Monitor pod availability, inspect source-level incoming test
                trends, and review execution and pod status distributions.
              </p>
            </div>
            <div className="adsure-header-controls">
              <MosaicFetchControlCard />
              <div className="adsure-header-metric">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <div className="adsure-header-metric-label">Pods Found</div>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="d-inline-flex align-items-center gap-2"
                  >
                    <BsArrowClockwise style={{ opacity: isFetching ? 0.7 : 1 }} />
                    {isFetching ? 'Refreshing' : 'Refresh'}
                  </Button>
                </div>
                <div className="adsure-header-metric-value">{pods.length}</div>
                <div className="small text-muted mt-2">
                  Sources: {monitoringSources.map(getSourceLabel).join(', ')}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="adsure-panel">
          <Tabs
            id="adsure-monitoring-tabs"
            activeKey={activeTab}
            onSelect={selectedKey => setActiveTab(selectedKey || 'pod-monitoring')}
            className="adsure-tabs"
          >
            <Tab eventKey="pod-monitoring" title="Pod Monitoring">
              <div className="adsure-tab-body">
                <PodMonitoringTab
                  pods={pods}
                  filteredPods={filteredPods}
                  podSearch={podSearch}
                  setPodSearch={setPodSearch}
                  sources={monitoringSources}
                  selectedSource={selectedSource}
                  onSourceChange={setSelectedSource}
                  isLoading={isLoading}
                  error={error}
                  isFetching={isFetching}
                  refetch={refetch}
                />
              </div>
            </Tab>
            <Tab eventKey="inflow" title="Incoming Tests">
              <div className="adsure-tab-body">
                <InflowTab
                  analytics={analytics}
                  isLoading={isAnalyticsLoading}
                  isFetching={isAnalyticsFetching}
                  error={analyticsError}
                  period={period}
                  startDate={startDate}
                  endDate={endDate}
                  selectedSource={selectedSource}
                  sources={monitoringSources}
                  onPeriodChange={setPeriod}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                  onSourceChange={setSelectedSource}
                />
              </div>
            </Tab>
            <Tab eventKey="outflow" title="Execution Analytics">
              <div className="adsure-tab-body">
                <OutflowTab
                  analytics={analytics}
                  isLoading={isAnalyticsLoading}
                  isFetching={isAnalyticsFetching}
                  error={analyticsError}
                  period={period}
                  startDate={startDate}
                  endDate={endDate}
                  selectedSource={selectedSource}
                  selectedPod={selectedAnalyticsPod}
                  sources={monitoringSources}
                  onPeriodChange={setPeriod}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                  onSourceChange={setSelectedSource}
                  onPodChange={setSelectedAnalyticsPod}
                />
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>
    </AdsureMonitoringStyles>
  );
}
